import _extends from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var _process$env$NEXT_PUB;
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useMediaQuery } from "react-responsive";
import moment from "moment";

// Components
import { MuscleButton } from "@modules/shared/components/buttons";
import MuscleDateRangeCalendar from "@modules/shared/components/date-calendars/date-range-calendar";
import MuscleSingleDateCalendar from "@modules/shared/components/date-calendars/single-date-calendar";
import MuscleFormDateInput from "@modules/shared/components/form-date-input";
import CalendarPricesFooter from "@modules/shared/components/calendar-prices-footer";
import InformativeCard from "@modules/shared/components/cards/informative";

// Hooks and Context
import { useSideOverPanel } from "@modules/mobile/hooks/useSideOverPanel";
import { useFlightsSearch } from "@lib/context/flights/search-context";
import { useMobileFilters } from "@lib/context/mobile/filters-context";
import { useFlightSelection } from "@lib/hooks/use-flight-selection";

// Types and Utils

import { dateFromUTC } from "@lib/util/date-ops";
import { BREAKPOINTS } from "@lib/constants";
import { FlightType } from "types/flights/search";
import flightsManager from "@lib/data/managers/flights/flights-manager";
import useCssModules from "@lib/hooks/use-css-modules";
import { cn } from "@lib/cn";
import { usePathname } from "next/navigation";
import { useForwardNode } from "@modules/mobile/hooks/useForwardNode";
const MAX_DAYS = Number((_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_MAX_CALENDAR_FLIGHT_DAYS) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : "365") || 365;
const ALLOW_PRICE_IN_CALENDAR = process.env.NEXT_PUBLIC_ALLOW_PRICE_IN_CALENDAR === "true";
const SHOW_PRICE_IN_DAY = process.env.NEXT_PUBLIC_SHOW_PRICE_IN_DAY === "true";
const useCalendarConfig = () => {
  const minDate = moment().toDate();
  const maxDate = moment().add(MAX_DAYS, "days").toDate();
  return {
    minDate,
    maxDate
  };
};
const useFlightInfo = () => {
  const {
    search,
    currentFlightIndex
  } = useFlightsSearch();
  const {
    currentFlight
  } = useFlightSelection(currentFlightIndex);
  return useMemo(() => {
    return currentFlight;
  }, [currentFlight]);
};
const useCalendarPricing = (currentFlightInfo, currentFlightType) => {
  const {
    search
  } = useFlightsSearch();
  const {
    flightFilters
  } = useMobileFilters();
  const {
    0: calendarData,
    1: setCalendarData
  } = useState();
  const {
    0: minPeriod,
    1: setMinPeriod
  } = useState(0);
  const {
    0: maxPeriod,
    1: setMaxPeriod
  } = useState(0);
  const {
    0: lastDates,
    1: setLastDates
  } = useState([undefined, undefined]);
  const {
    0: currentRangeIdentifier,
    1: setCurrentRangeIdentifier
  } = useState("");
  const getPeriod = useCallback(date => {
    return date.getFullYear() * 12 + date.getMonth();
  }, []);
  const fetchCalendarData = useCallback(async (month, selectedDates) => {
    if (!ALLOW_PRICE_IN_CALENDAR || !search) return undefined;
    try {
      if (currentFlightType === FlightType.MULTI_DESTINATION) return undefined;
      if (!currentFlightInfo.origin_iata || !currentFlightInfo.destination_iata) return undefined;
      const calendarFlightInfo = [currentFlightInfo];
      calendarFlightInfo.forEach(itinerary => {
        if (currentFlightType === FlightType.ROUND_TRIP) {
          var _selectedDates$;
          const from = selectedDates[0] || month;
          itinerary.departure_date = from.toISOString();
          itinerary.return_date = (_selectedDates$ = selectedDates[1]) === null || _selectedDates$ === void 0 ? void 0 : _selectedDates$.toISOString();
        } else {
          let from = selectedDates[0] || month;
          if (selectedDates[0] && (selectedDates[0].getMonth() !== month.getMonth() || selectedDates[0].getFullYear() !== month.getFullYear())) {
            from = month;
          }
          itinerary.departure_date = from.toISOString();
          itinerary.return_date = undefined;
        }
      });
      const searchCalendarData = await flightsManager.getMonthlyFlightCalendar({
        flight_type: currentFlightType,
        passengers_details: flightFilters.passengersDetails,
        flight_info: calendarFlightInfo,
        fully_priced: SHOW_PRICE_IN_DAY
      });
      if (searchCalendarData.data) {
        return _objectSpread(_objectSpread({}, searchCalendarData), {}, {
          data: searchCalendarData.data.map(price => _objectSpread(_objectSpread({}, price), {}, {
            date: dateFromUTC(price.date),
            classification: price.classification
          }))
        });
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  }, [search, currentFlightType, currentFlightInfo, flightFilters.passengersDetails]);
  const changeMonth = useCallback(function (month, selectedDates) {
    let force = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (lastDates[1] === selectedDates[1] && getPeriod(month) >= minPeriod && getPeriod(month) <= maxPeriod && !force && currentRangeIdentifier === "calendar-selection") {
      return;
    }
    setCurrentRangeIdentifier("calendar-selection");
    fetchCalendarData(month, selectedDates).then(data => {
      var _data$data;
      setCalendarData(data);
      if (data !== null && data !== void 0 && (_data$data = data.data) !== null && _data$data !== void 0 && _data$data.length) {
        setMinPeriod(Math.min(...data.data.map(price => price.period)));
        setMaxPeriod(Math.max(...data.data.map(price => price.period)));
        setLastDates([selectedDates[0], selectedDates[1]]);
      }
    });
  }, [fetchCalendarData, lastDates, minPeriod, maxPeriod, currentRangeIdentifier]);
  return {
    calendarData,
    changeMonth,
    fetchCalendarData
  };
};

// Main Component
export const CalendarSelectionView = () => {
  const t = useTranslations();
  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINTS.md - 1
  });
  const {
    search,
    setSearch,
    currentFlightIndex
  } = useFlightsSearch();
  const {
    togglePanel
  } = useSideOverPanel();
  const {
    cssModules
  } = useCssModules();
  const pathname = usePathname();
  const {
    setNode
  } = useForwardNode();
  const {
    calendarSelectionPanelContainer,
    calendarSelectionApplyButtonContainer,
    calendarSelectionInputsContainer,
    calendarSelectionPricesContainer
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.views.mobile.CalendarSelection) || {};

  // Configuration and state
  const {
    minDate,
    maxDate
  } = useCalendarConfig();
  const currentFlightInfo = useFlightInfo();
  const currentFlightType = (search === null || search === void 0 ? void 0 : search.flight_type) || FlightType.ROUND_TRIP;
  const isDateRangeMode = currentFlightType === FlightType.ROUND_TRIP;
  const {
    calendarData,
    changeMonth
  } = useCalendarPricing(currentFlightInfo, currentFlightType);

  // Selected dates state
  const {
    0: selectedDates,
    1: setSelectedDates
  } = useState([currentFlightInfo !== null && currentFlightInfo !== void 0 && currentFlightInfo.departure_date ? new Date(currentFlightInfo.departure_date) : undefined, currentFlightInfo !== null && currentFlightInfo !== void 0 && currentFlightInfo.return_date ? new Date(currentFlightInfo.return_date) : undefined]);
  const {
    0: currentMonth,
    1: setCurrentMonth
  } = useState();

  // Update selected dates when context changes
  useEffect(() => {
    if (currentFlightInfo) {
      setSelectedDates([currentFlightInfo.departure_date ? new Date(currentFlightInfo.departure_date) : undefined, currentFlightInfo.return_date ? new Date(currentFlightInfo.return_date) : undefined]);
    }
  }, [currentFlightInfo]);

  // Initialize calendar data
  useEffect(() => {
    changeMonth(minDate, selectedDates, true);
  }, []);

  // Disclaimer text
  const disclaimerText = useMemo(() => {
    const disclaimerByFlightType = {
      [FlightType.ROUND_TRIP]: t("Flights.DISCLAIMER_CALENDAR_ROUND_TRIP"),
      [FlightType.ONE_WAY]: t("Flights.DISCLAIMER_CALENDAR_ONE_WAY"),
      [FlightType.MULTI_DESTINATION]: undefined
    };
    return disclaimerByFlightType[currentFlightType];
  }, [currentFlightType, t]);

  // Event handlers
  const handleChangeDate = useCallback((from, to) => {
    setSelectedDates([from, to]);

    // Find the current flight by ID and update only that flight
    const flightIndex = search === null || search === void 0 ? void 0 : search.flight_info.findIndex(flight => flight.id === currentFlightIndex + 1);
    if (flightIndex !== undefined && flightIndex !== -1 && search) {
      const updatedFlightInfoArray = [...search.flight_info];
      updatedFlightInfoArray[flightIndex] = _objectSpread(_objectSpread({}, updatedFlightInfoArray[flightIndex]), {}, {
        departure_date: from === null || from === void 0 ? void 0 : from.toISOString(),
        return_date: to === null || to === void 0 ? void 0 : to.toISOString()
      });
      setSearch(_objectSpread(_objectSpread({}, search), {}, {
        flight_info: updatedFlightInfoArray
      }));
    }
  }, [search, currentFlightIndex, setSearch]);
  const handleCalendarSelect = useCallback(dateRange => {
    handleChangeDate(dateRange.from, dateRange.to);
    changeMonth(currentMonth || minDate, [dateRange.from, dateRange.to], true);
  }, [handleChangeDate, changeMonth, currentMonth, minDate]);
  const handleSingleDateSelect = useCallback(date => {
    handleChangeDate(date, undefined);
    changeMonth(currentMonth || minDate, [date, undefined]);
  }, [handleChangeDate, changeMonth, currentMonth, minDate]);
  const handleMonthChange = useCallback(month => {
    setCurrentMonth(month);
    changeMonth(month, selectedDates, true);
  }, [changeMonth, selectedDates]);
  const isDateSelectionValid = useMemo(() => {
    if (isDateRangeMode) {
      return selectedDates[0] !== undefined && selectedDates[1] !== undefined;
    }
    return selectedDates[0] !== undefined;
  }, [isDateRangeMode, selectedDates]);

  // Render helpers
  const renderCalendar = () => {
    const commonProps = {
      minDate,
      maxDate,
      prices: calendarData === null || calendarData === void 0 ? void 0 : calendarData.data,
      isMobile
    };
    if (isDateRangeMode) {
      return __jsx(MuscleDateRangeCalendar, _extends({}, commonProps, {
        onSelect: handleCalendarSelect,
        value: {
          from: selectedDates[0],
          to: selectedDates[1]
        },
        onMonthChange: handleMonthChange
      }));
    }
    return __jsx(MuscleSingleDateCalendar, _extends({}, commonProps, {
      onSelect: handleSingleDateSelect,
      value: selectedDates[0],
      onMonthChange: handleMonthChange
    }));
  };
  const renderPriceFooter = () => {
    var _calendarData$data, _calendarData$lowest_, _calendarData$highest, _calendarData$median_;
    if (!(calendarData !== null && calendarData !== void 0 && (_calendarData$data = calendarData.data) !== null && _calendarData$data !== void 0 && _calendarData$data.length)) return null;
    return __jsx("div", {
      className: calendarSelectionPricesContainer
    }, __jsx(CalendarPricesFooter, {
      minWidthFitIfCan: false,
      lowestFrom: (_calendarData$lowest_ = calendarData.lowest_from) !== null && _calendarData$lowest_ !== void 0 ? _calendarData$lowest_ : 0,
      highestFrom: (_calendarData$highest = calendarData.highest_from) !== null && _calendarData$highest !== void 0 ? _calendarData$highest : 0,
      mediumFrom: (_calendarData$median_ = calendarData.median_from) !== null && _calendarData$median_ !== void 0 ? _calendarData$median_ : 0
    }), disclaimerText && __jsx(InformativeCard, {
      message: disclaimerText,
      severity: "info",
      className: "w-full tx-pxs-light text-primary_text"
    }));
  };
  const renderDateInputs = () => {
    var _selectedDates$2, _selectedDates$3, _selectedDates$4;
    return __jsx("div", {
      className: calendarSelectionInputsContainer
    }, __jsx(MuscleFormDateInput, {
      value: (_selectedDates$2 = selectedDates[0]) === null || _selectedDates$2 === void 0 ? void 0 : _selectedDates$2.toISOString(),
      onChange: dateString => {
        const date = dateString ? new Date(dateString) : undefined;
        if (isDateRangeMode) {
          handleChangeDate(date, selectedDates[1]);
        } else {
          handleChangeDate(date, undefined);
        }
      },
      minDate: minDate.toISOString(),
      maxDate: maxDate.toISOString(),
      className: cn("flex-1 ", isDateRangeMode && "!max-w-[100px]"),
      hideEmptyLabel: true,
      hideGuideText: true,
      hideErrorMessage: true
    }), isDateRangeMode && __jsx(React.Fragment, null, __jsx("span", {
      className: "text-primary_text tx-pmd-regular"
    }, t("Shared.TO_THE")), __jsx(MuscleFormDateInput, {
      value: (_selectedDates$3 = selectedDates[1]) === null || _selectedDates$3 === void 0 ? void 0 : _selectedDates$3.toISOString(),
      onChange: dateString => {
        const date = dateString ? new Date(dateString) : undefined;
        handleChangeDate(selectedDates[0], date);
      },
      minDate: ((_selectedDates$4 = selectedDates[0]) === null || _selectedDates$4 === void 0 ? void 0 : _selectedDates$4.toISOString()) || minDate.toISOString(),
      maxDate: maxDate.toISOString(),
      className: "flex-1 !max-w-[100px]",
      hideEmptyLabel: true,
      hideGuideText: true,
      hideErrorMessage: true
    })));
  };
  const renderButtons = () => __jsx("div", {
    className: calendarSelectionApplyButtonContainer
  }, __jsx(MuscleButton, {
    dataTestId: "calendar-choose-dates-button",
    onClick: pathname.includes("/flights/select") ? () => setNode("EditFlightSelection") : togglePanel,
    disabled: !isDateSelectionValid,
    variant: "solidXL"
  }, t("Shared.CHOOSE_DATES")), pathname.includes("/flights/select") ? __jsx(MuscleButton, {
    dataTestId: "calendar-cancel-button",
    onClick: () => setNode("EditFlightSelection"),
    variant: "outlineXL"
  }, t("Shared.MAKE_CHANGES")) : __jsx(MuscleButton, {
    dataTestId: "calendar-cancel-button",
    onClick: togglePanel,
    variant: "outlineXL"
  }, t("Shared.CANCEL")));
  return __jsx("div", {
    className: calendarSelectionPanelContainer
  }, renderCalendar(), renderPriceFooter(), renderDateInputs(), renderButtons());
};